import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Mail, Plus, Trash2, Save, Loader2, Clock, Calendar } from 'lucide-react';
import { getDefaultRecipients, saveDefaultRecipients, getScheduledReportsConfig, saveScheduledReportsConfig } from '../utils/api';
import { toast } from 'sonner';
import AgentLayout from '../components/AgentLayout';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [recipients, setRecipients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [scheduleConfig, setScheduleConfig] = useState({
    enabled: true, dayOfWeek: 0, time: '12:00', timezone: 'US/Eastern',
    dayLabel: 'Sunday', sendActivity: true, sendGoals: true,
  });
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);

  const token = localStorage.getItem('agentToken');
  const DAY_OPTIONS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    if (!token) { navigate('/agent/login'); return; }
    loadSettings();
  }, [navigate]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const [recRes, schedRes] = await Promise.all([
        getDefaultRecipients(token),
        getScheduledReportsConfig(token),
      ]);
      setRecipients(recRes.recipients || []);
      if (schedRes.config) setScheduleConfig(schedRes.config);
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const addRecipient = () => {
    const name = newName.trim();
    const email = newEmail.trim().toLowerCase();
    if (!name || !email || !email.includes('@')) {
      toast.error('Enter a valid name and email');
      return;
    }
    if (recipients.find(r => r.email === email)) {
      toast.error('Email already added');
      return;
    }
    setRecipients(prev => [...prev, { name, email }]);
    setNewName('');
    setNewEmail('');
  };

  const removeRecipient = (index) => {
    setRecipients(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveDefaultRecipients(recipients, token);
      toast.success('Default recipients saved!');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSchedule = async () => {
    setIsSavingSchedule(true);
    try {
      const updated = { ...scheduleConfig, dayLabel: DAY_OPTIONS[scheduleConfig.dayOfWeek] };
      await saveScheduledReportsConfig(updated, token);
      setScheduleConfig(updated);
      toast.success('Schedule configuration saved!');
    } catch (e) {
      toast.error('Failed to save schedule');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  if (isLoading) {
    return (
      <AgentLayout title="Admin Settings">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Admin Settings">
      <div data-testid="admin-settings-page">

        {/* Default Report Recipients */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-testid="default-recipients-section">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={20} className="text-brand-blue" />
            <h2 className="text-lg font-bold text-gray-900">Default Report Recipients</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            These contacts will appear as quick-select options when agents send reports via email. Use this to add trainer emails like Fred Hawkins.
          </p>

          {/* Existing recipients */}
          {recipients.length > 0 && (
            <div className="space-y-2 mb-4">
              {recipients.map((r, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3" data-testid={`recipient-row-${i}`}>
                  <div>
                    <span className="font-semibold text-gray-900 text-sm">{r.name}</span>
                    <span className="text-gray-400 text-sm ml-2">{r.email}</span>
                  </div>
                  <button
                    onClick={() => removeRecipient(i)}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                    data-testid={`remove-recipient-${i}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {recipients.length === 0 && (
            <div className="text-center py-6 text-gray-400 mb-4">
              <Mail size={32} className="mx-auto mb-2" />
              <p className="text-sm">No default recipients yet. Add one below.</p>
            </div>
          )}

          {/* Add new */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name (e.g. Fred Hawkins)"
              className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none"
              data-testid="new-recipient-name"
            />
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); }}}
              placeholder="Email address"
              className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none"
              data-testid="new-recipient-email"
            />
            <button
              onClick={addRecipient}
              className="flex items-center gap-1 px-4 py-2 bg-brand-blue text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition min-h-[44px]"
              data-testid="add-recipient-btn"
            >
              <Plus size={16} /> Add
            </button>
          </div>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2.5 bg-brand-blue text-white rounded-lg text-sm font-semibold hover:bg-brand-orange transition disabled:opacity-50 min-h-[44px]"
            data-testid="save-recipients-btn"
          >
            {isSaving ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : <><Save size={16} /> Save Recipients</>}
          </button>
        </div>

        {/* Scheduled Report Config */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-testid="scheduled-reports-section">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={20} className="text-brand-blue" />
            <h2 className="text-lg font-bold text-gray-900">Automatic Report Schedule</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Configure when weekly reports are automatically emailed to the default recipients above. All active agents' reports will be sent at this time.
          </p>

          <div className="space-y-4">
            {/* Enable toggle */}
            <label className="flex items-center gap-3 cursor-pointer" data-testid="schedule-enabled-toggle">
              <input
                type="checkbox"
                checked={scheduleConfig.enabled}
                onChange={(e) => setScheduleConfig({ ...scheduleConfig, enabled: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-brand-blue focus:ring-brand-blue cursor-pointer"
              />
              <span className="text-sm font-semibold text-gray-700">Enable automatic report sending</span>
            </label>

            {scheduleConfig.enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pl-1">
                {/* Day of week */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Day of Week</label>
                  <select
                    value={scheduleConfig.dayOfWeek}
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, dayOfWeek: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none"
                    data-testid="schedule-day-select"
                  >
                    {DAY_OPTIONS.map((day, i) => (
                      <option key={i} value={i}>{day}</option>
                    ))}
                  </select>
                </div>

                {/* Time */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Time</label>
                  <input
                    type="time"
                    value={scheduleConfig.time}
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, time: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none"
                    data-testid="schedule-time-input"
                  />
                </div>

                {/* Timezone */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Timezone</label>
                  <div className="px-3 py-2 bg-gray-50 border rounded-lg text-sm text-gray-600" data-testid="schedule-timezone">
                    US/Eastern (ET)
                  </div>
                </div>
              </div>
            )}

            {scheduleConfig.enabled && (
              <div className="flex gap-4 pl-1">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="schedule-send-activity">
                  <input
                    type="checkbox"
                    checked={scheduleConfig.sendActivity}
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, sendActivity: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Send Activity Reports</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="schedule-send-goals">
                  <input
                    type="checkbox"
                    checked={scheduleConfig.sendGoals}
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, sendGoals: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Send Goals Reports</span>
                </label>
              </div>
            )}

            {/* Current schedule summary */}
            {scheduleConfig.enabled && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700" data-testid="schedule-summary">
                <Calendar size={14} className="inline mr-1.5" />
                Reports will auto-send every <strong>{DAY_OPTIONS[scheduleConfig.dayOfWeek]}</strong> at <strong>{scheduleConfig.time}</strong> ET
                {scheduleConfig.sendActivity && scheduleConfig.sendGoals ? ' (Activity + Goals)' :
                  scheduleConfig.sendActivity ? ' (Activity only)' :
                  scheduleConfig.sendGoals ? ' (Goals only)' : ''}
              </div>
            )}

            <button
              onClick={handleSaveSchedule}
              disabled={isSavingSchedule}
              className="flex items-center gap-2 px-6 py-2.5 bg-brand-blue text-white rounded-lg text-sm font-semibold hover:bg-brand-orange transition disabled:opacity-50 min-h-[44px]"
              data-testid="save-schedule-btn"
            >
              {isSavingSchedule ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : <><Save size={16} /> Save Schedule</>}
            </button>
          </div>
        </div>

      </div>
    </AgentLayout>
  );
};

export default AdminSettings;
